import { useEffect, useMemo, useState } from 'react';
import { demeterMarketIndicatorsApi } from '../../../../Apis/Apis';
import { DemeterUserStoreType, MarketIndicatorModel, MarketIndicatorOutlook } from '../../../../Generated/Raven-Demeter';
import useCacheThenApi from '../../../Apis/Hooks/useCacheThenApiHook';
import useUserStoreApi from '../../../Apis/Hooks/useUserStoreApiHook';
import IndicatorsManagementButton from '../../../Components/Form/Buttons/IndicatorsManagementButton/IndicatorsManagementButton';
import CardSkeleton from '../../../Components/Layout/CardSkeleton';
import ManageMarketIndicatorsModal from '../../../Components/Modals/ProductsModal/ManageCommodityIndicatorsModal';
import CacheKeys from '../../../Services/Cache/CacheKeys';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import HedgeMonitorProSummary from '../../HedgeMonitorPro/HedgeMonitorProSummary';
import styles from './CustomDefaultDashboardComponents.module.scss';

interface ICustomDashboardHedgeMonitorProIndicatorsProps {
    useDynamicHeight?: boolean;
    track: TrackingEvent;
}

const CustomDashboardHedgeMonitorProIndicators: React.FC<ICustomDashboardHedgeMonitorProIndicatorsProps> = (
    props: ICustomDashboardHedgeMonitorProIndicatorsProps,
) => {
    // Application hooks.
    const [translations] = useLanguage();
    const [userMarketIndicatorsResponse, updateUserMarketIndicators] = useUserStoreApi(DemeterUserStoreType.UserMarketIndicators);
    const [manageCommodityIndicatorsModalOpen, setManageCommodityIndicatorsModalOpen] = useState(false);
    const [userMarketIndicators, setUserMarketIndicators] = useState<string[] | undefined>(undefined);

    // Text hooks.
    const marketIndicatorTitleString = useMemo(() => translations.dashboard.headings.hedgeMonitorProHeader, [translations]);

    // Api hooks.
    const [isLoading, , listMarketIndicatorsResponse] = useCacheThenApi(CacheKeys.ListMarketIndicatorsCurrent, () =>
        demeterMarketIndicatorsApi.listMarketIndicatorsCurrent(),
    );

    const marketIndicators = useMemo(
        () =>
            listMarketIndicatorsResponse?.rows?.filter(
                (x) => x.outlook !== MarketIndicatorOutlook.Unavailable && x.hedgeMonitorProOutlook && x.precipiceOutlook,
            ) ?? [],
        [listMarketIndicatorsResponse],
    );

    const selectedMarketIndicators = useMemo(() => {
        if (!marketIndicators || !userMarketIndicators) {
            return null;
        }

        const selected: MarketIndicatorModel[] = [];
        userMarketIndicators.forEach((val) => {
            const match = marketIndicators.find((x) => x.marketIndicatorGuid === val);
            if (match) {
                selected.push(match);
            }
        });

        return selected;
    }, [marketIndicators, userMarketIndicators]);

    useEffect(() => setUserMarketIndicators(userMarketIndicatorsResponse?.userStore?.value?.userMarketIndicatorGuids ?? []), [userMarketIndicatorsResponse]);

    const handleUserMarketIndicatorsUpdate = async (indicators: MarketIndicatorModel[]) => {
        const updatedUserMarketIndicators = indicators?.map((x) => x.marketIndicatorGuid);
        setUserMarketIndicators(updatedUserMarketIndicators);
        updateUserMarketIndicators({
            userMarketIndicatorGuids: updatedUserMarketIndicators,
        });
    };

    const showLoadingSpinner = isLoading || !selectedMarketIndicators;

    const containerClassName = props.useDynamicHeight ? styles.hedgeMonitorPro_container_dynamic_height : styles.hedgeMonitorPro_container;
    const indicatorsClassName = props.useDynamicHeight ? styles.hedgeMonitorPro_outlook_indicators_dynamic_height : styles.hedgeMonitorPro_outlook_indicators;

    return showLoadingSpinner ? (
        <CardSkeleton />
    ) : (
        <div className={containerClassName}>
            <div data-testid="DashboardHedgeMonitorProIndicators">
                <div className={indicatorsClassName}>
                    {selectedMarketIndicators?.map((x) => (
                        <div className={styles.hedgeMonitorPro_outlook_indicator} key={x.marketIndicatorGuid}>
                            <HedgeMonitorProSummary useNavigationCard marketIndicator={x} track={props.track} />
                        </div>
                    ))}
                    <IndicatorsManagementButton
                        handleClick={() => setManageCommodityIndicatorsModalOpen(true)}
                        tooltipText={translations.dashboard.indicators.addOrRemoveHedgeMonitorProIndicators}
                        track={{ eventCategory: 'Dashboard', eventAction: 'OpenManageHedgeMonitorProModalClicked' }}
                    />
                    <ManageMarketIndicatorsModal
                        open={manageCommodityIndicatorsModalOpen}
                        title={marketIndicatorTitleString}
                        selected={selectedMarketIndicators}
                        indicators={marketIndicators}
                        handleClose={() => setManageCommodityIndicatorsModalOpen(false)}
                        handleChange={handleUserMarketIndicatorsUpdate}
                    />
                </div>
            </div>
        </div>
    );
};

export default CustomDashboardHedgeMonitorProIndicators;
